import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { bookingsAPI, locationsAPI } from '../../api/bookings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Calendar, MapPin, Users, X, Unlock } from 'lucide-react';
import BottomNav from '../../components/BottomNav';
import { formatDate, getStatusColor, getSportColor } from '../../lib/utils';
import { formatBookingReference, formatPaymentMethod } from '../../utils/bookingUtils';
import DateTimeDisplay from '../../components/booking/DateTimeDisplay';
import { toast } from 'sonner';
import { sortBookingsByPriority } from '../../lib/bookingSort';

export default function BookingsList() {
  const navigate = useNavigate();
  const { user, isGuest } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [locations, setLocations] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [cancellingWaitlist, setCancellingWaitlist] = useState(null);
  const [waitlistCancelTarget, setWaitlistCancelTarget] = useState(null); // entry to confirm-cancel

  // Re-used twice in the JSX below (count check + render). Memoize so the
  // filter doesn't run on every render (waitlistEntries is fetched once
  // per page load but rerenders triggered by other state shouldn't refilter).
  const activeWaitlist = useMemo(
    () => waitlistEntries.filter((e) => e.status !== 'CANCELLED' && e.status !== 'EXPIRED'),
    [waitlistEntries],
  );

  const loadLocations = useCallback(async () => {
    try {
      const locs = await locationsAPI.getLocations();
      const locsMap = {};
      locs.forEach(loc => {
        locsMap[loc.id] = loc;
      });
      setLocations(locsMap);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  }, []);

  const loadBookings = useCallback(async () => {
    if (!user) return;
    try {
      let userBookings;
      if (isGuest && user.phone) {
        userBookings = await bookingsAPI.getGuestBookings(user.phone);
      } else if (user.id) {
        userBookings = await bookingsAPI.getUserBookings(user.id);
      } else {
        userBookings = [];
      }
      setBookings(userBookings || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [user, isGuest]);

  const loadWaitlist = useCallback(async () => {
    if (!user) return;
    try {
      let userWaitlist;
      if (isGuest && user.phone) {
        userWaitlist = await bookingsAPI.getGuestWaitlist(user.phone);
      } else if (user.id) {
        userWaitlist = await bookingsAPI.getUserWaitlist(user.id);
      } else {
        userWaitlist = [];
      }
      setWaitlistEntries(userWaitlist || []);
    } catch (error) {
      console.error('Error loading waitlist:', error);
    }
  }, [user, isGuest]);

  useEffect(() => {
    // Auth gate lives in <CustomerProtectedRoute /> (see App.js) — by the
    // time we render, `user` is guaranteed non-null.
    loadLocations();
    loadBookings();
    loadWaitlist();
  }, [user, loadLocations, loadBookings, loadWaitlist]);
  
  const handleCancelWaitlist = async (entryId) => {
    setCancellingWaitlist(entryId);
    try {
      await bookingsAPI.cancelWaitlistEntry(entryId);
      toast.success('Removed from waitlist');
      loadWaitlist();
    } catch (error) {
      toast.error('Failed to cancel waitlist entry');
    } finally {
      setCancellingWaitlist(null);
      setWaitlistCancelTarget(null);
    }
  };
  
  const getWaitlistStatusColor = (status) => {
    switch(status) {
      case 'WAITING': return 'bg-blue-100 text-blue-800';
      case 'NOTIFIED': return 'bg-green-100 text-green-800';
      case 'CONFIRMED': return 'bg-purple-100 text-purple-800';
      case 'EXPIRED': return 'bg-gray-100 text-gray-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredBookings = sortBookingsByPriority(
    bookings.filter((b) => {
      if (filter === 'all') return true;
      return b.status === filter;
    }),
  );

  // Per-status counts feed the chip-pill badges below. Computed once per
  // render from the already-loaded `bookings` (no extra fetches).
  const statusCounts = useMemo(() => {
    const counts = { all: bookings.length };
    for (const b of bookings) {
      counts[b.status] = (counts[b.status] || 0) + 1;
    }
    return counts;
  }, [bookings]);

  // Status chips replace the prior grid-cols-5 TabsList. Each chip
  // carries its accent color (dot + active fill) so the bar reads as a
  // status scan, not just navigation. Wraps to 2 lines on narrow phones
  // instead of overflowing. Zero-count chips stay tappable but greyed
  // out — preserves muscle memory.
  const STATUS_CHIPS = [
    { value: 'all',                label: 'All',           dot: 'bg-slate-500',   activeBg: 'bg-slate-900 text-white border-slate-900' },
    { value: 'PENDING_ALLOCATION', label: 'Pending',       dot: 'bg-amber-500',   activeBg: 'bg-amber-500 text-white border-amber-500' },
    { value: 'AWAITING_PAYMENT',   label: 'Awaiting Pay',  dot: 'bg-sky-500',     activeBg: 'bg-sky-600 text-white border-sky-600' },
    { value: 'CONFIRMED',          label: 'Confirmed',     dot: 'bg-emerald-500', activeBg: 'bg-emerald-600 text-white border-emerald-600' },
    { value: 'RELEASED',           label: 'Released',      dot: 'bg-sky-500',     activeBg: 'bg-sky-600 text-white border-sky-600' },
    { value: 'CANCELLED',          label: 'Cancelled',     dot: 'bg-slate-400',   activeBg: 'bg-slate-600 text-white border-slate-600' },
  ];

  return (
    <div className="mobile-container min-h-screen pb-20">
      <div className="bg-primary text-primary-foreground p-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/home')}
            data-testid="back-button"
            className="text-primary-foreground hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-black font-heading uppercase">My Bookings</h1>
        </div>
      </div>

      <div className="p-6" data-testid="bookings-list">
        <Tabs value={filter} onValueChange={setFilter} className="w-full">
          {/* Status chip-pill row — replaces the squashed grid-cols-5 tabs.
              Wraps to a second line on narrow phones instead of overflowing.
              Each chip shows its count when ≥1 (else hidden for cleanliness)
              and greys out tappably when the count is 0 (muscle memory). */}
          <div
            role="tablist"
            aria-label="Filter bookings by status"
            className="flex flex-wrap gap-2 mb-6"
            data-testid="booking-status-chips"
          >
            {STATUS_CHIPS.map((chip) => {
              const count = statusCounts[chip.value] || 0;
              const isActive = filter === chip.value;
              const isEmpty = count === 0;
              const showCount = count >= 1;
              return (
                <button
                  key={chip.value}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setFilter(chip.value)}
                  data-testid={`status-chip-${chip.value.toLowerCase()}`}
                  className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-sm font-semibold transition-colors ${
                    isActive
                      ? chip.activeBg + ' shadow-sm'
                      : isEmpty
                        ? 'bg-white text-slate-400 border-slate-200'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${chip.dot} ${
                      isEmpty && !isActive ? 'opacity-40' : ''
                    }`}
                    aria-hidden="true"
                  />
                  <span>{chip.label}</span>
                  {showCount && (
                    <span
                      className={`min-w-[1.25rem] text-center text-[11px] font-bold rounded-full px-1.5 ${
                        isActive
                          ? 'bg-white/25 text-white'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                      data-testid={`status-chip-count-${chip.value.toLowerCase()}`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <TabsContent value={filter}>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredBookings.length === 0 ? (
              <Card className="p-8 text-center glass-card">
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No bookings found</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredBookings.map((booking) => (
                  <Card
                    key={booking.id}
                    onClick={() => navigate(`/booking/${booking.id}`)}
                    data-testid="booking-card"
                    className="p-4 glass-card hover:border-accent/50 transition-all cursor-pointer court-card"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`status-badge ${getStatusColor(booking.status)}`}>
                            {booking.status.replace('_', ' ')}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {formatBookingReference(booking.id)}
                          </span>
                          {booking.payment_method && booking.status === 'CONFIRMED' && (
                            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
                              {formatPaymentMethod(booking.payment_method)}
                            </span>
                          )}
                        </div>
                        <DateTimeDisplay
                          date={booking.booking_date}
                          timeSlot={booking.time_slot}
                          duration={booking.duration}
                          className="mt-1"
                          testId="bookings-list"
                        />
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-xl">${(booking.status === 'PENDING_ALLOCATION'
                          ? (booking.estimated_total ?? booking.final_total ?? 0)
                          : (booking.final_total ?? booking.estimated_total ?? 0)
                        ).toFixed(2)}</p>
                      </div>
                    </div>
                    {/* Location */}
                    {locations[booking.location_id] && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{locations[booking.location_id].name}</span>
                      </div>
                    )}
                    <div className="flex gap-2 mt-2">
                      {booking.courts_requested.map((court, idx) => (
                        <span key={`${court.sport}-${idx}`} className={`sport-badge ${getSportColor(court.sport)}`}>
                          {court.sport}
                        </span>
                      ))}
                    </div>
                    {/* Cancellation context — only on cancelled bookings.
                        Surfaces "Cancelled by you / by SmashZone team" + date
                        so the customer doesn't have to wonder who initiated. */}
                    {booking.status === 'CANCELLED' && (
                      <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground" data-testid="customer-cancel-meta">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                          Cancelled by{' '}
                          {(() => {
                            const cb = (booking.cancelled_by || '').toLowerCase();
                            if (cb === 'customer') return 'you';
                            if (cb.startsWith('admin') || cb === 'manager') return 'SmashZone team';
                            return 'system';
                          })()}
                          {booking.cancelled_at ? (
                            <> · {new Date(booking.cancelled_at).toLocaleDateString()}</>
                          ) : null}
                        </span>
                        {booking.cancellation_reason && (
                          <span className="text-[11px] italic" title={booking.cancellation_reason}>
                            "{booking.cancellation_reason.length > 60
                              ? booking.cancellation_reason.slice(0, 60) + '…'
                              : booking.cancellation_reason}"
                          </span>
                        )}
                      </div>
                    )}
                    {/* Released — staff voluntarily freed an already-paid slot (e.g.
                        tournament need). Deliberately does NOT surface the staff's
                        internal `released_note` — that's an audit-trail field, not
                        customer-facing copy. Shows a generic reassurance line instead,
                        mirroring the wording in send_booking_released_email. */}
                    {booking.status === 'RELEASED' && (
                      <div className="flex flex-col gap-1 mt-2 text-xs text-muted-foreground" data-testid="customer-released-meta">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 w-fit">
                          <Unlock className="w-3 h-3" />
                          Released by SmashZone team
                          {booking.released_at ? (
                            <> · {new Date(booking.released_at).toLocaleDateString()}</>
                          ) : null}
                        </span>
                        <span>Your payment remains on file. We'll contact you to arrange a replacement, or reach out to us directly.</span>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        {/* My Waitlist Section */}
        <div className="mt-8 pt-6 border-t">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-black font-heading uppercase">My Waitlist</h2>
          </div>
          
          {loading ? (
            <div className="text-center py-4 text-muted-foreground">Loading...</div>
          ) : activeWaitlist.length === 0 ? (
            <Card className="p-6 text-center glass-card">
              <Calendar className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">You're not on any waitlists</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {activeWaitlist.map((entry) => (
                <Card
                  key={entry.id}
                  className="p-4 glass-card border-l-4 border-l-blue-500"
                  data-testid="waitlist-card"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`status-badge ${getWaitlistStatusColor(entry.status)}`}>
                          {entry.status}
                        </span>
                        {entry.status === 'WAITING' && entry.queue_position && (
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-bold">
                            Position #{entry.queue_position}
                          </span>
                        )}
                      </div>
                      <DateTimeDisplay
                        date={entry.preferred_date}
                        timeSlot={entry.preferred_time_slot}
                        duration={entry.duration}
                        className="mt-1"
                        testId="waitlist-entry"
                      />
                      {entry.duration && entry.duration > 1 && (
                        <span className="inline-block mt-2 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-bold">
                          {entry.duration}hr
                        </span>
                      )}
                    </div>
                    
                    {entry.status === 'WAITING' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setWaitlistCancelTarget(entry.id)}
                        disabled={cancellingWaitlist === entry.id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="cancel-waitlist-button"
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Location */}
                  {locations[entry.location_id] && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{locations[entry.location_id].name}</span>
                    </div>
                  )}
                  
                  {/* Sport */}
                  <div className="flex gap-2">
                    <span className={`sport-badge ${getSportColor(entry.sport)}`}>
                      {entry.sport} × {entry.courts_needed}
                    </span>
                  </div>
                  
                  {entry.status === 'NOTIFIED' && (
                    <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-xs font-medium text-green-900">
                        🎉 Good news! A court is available. Check your notifications!
                      </p>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Waitlist cancel confirmation dialog */}
      <AlertDialog
        open={!!waitlistCancelTarget}
        onOpenChange={(open) => { if (!open) setWaitlistCancelTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave waitlist?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll lose your current queue position. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="waitlist-cancel-dialog-cancel">Keep my spot</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleCancelWaitlist(waitlistCancelTarget)}
              disabled={cancellingWaitlist === waitlistCancelTarget}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="waitlist-cancel-dialog-confirm"
            >
              {cancellingWaitlist === waitlistCancelTarget ? 'Removing…' : 'Yes, leave'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
}